import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { BRAND, telLink, waLink } from "../../lib/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/epoxy-flooring", label: "Epoxy Flooring" },
  { to: "/waterproofing", label: "Waterproofing" },
  { to: "/painting", label: "Painting Services" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/90 border-b border-ink-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 shrink-0"
        >
          <span className="h-9 w-9 bg-ink text-white grid place-items-center font-display text-lg">
            U
          </span>
          <div className="leading-tight">
            <div className="font-display text-lg md:text-xl text-ink tracking-tight">
              {BRAND.name}
            </div>
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-ink-muted hidden sm:block">
              {BRAND.tagline}
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] transition-colors inline-flex items-center gap-1.5 ${
                  n.to === "/epoxy-flooring"
                    ? isActive
                      ? "text-orange font-semibold"
                      : "text-orange hover:text-orange-dark"
                    : isActive
                      ? "text-ink font-semibold"
                      : "text-ink-muted hover:text-ink"
                }`
              }
              end={n.to === "/"}
            >
              {n.to === "/epoxy-flooring" && (
                <span
                  aria-hidden
                  className="w-1.5 h-1.5 rounded-full bg-orange"
                  data-testid="nav-epoxy-highlight-dot"
                />
              )}
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <a
            href={telLink()}
            data-testid="nav-call"
            className="flex items-center gap-2 text-[13px] text-ink hover:text-ink-soft"
          >
            <Phone size={14} /> {BRAND.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-get-quote"
            className="bg-ink hover:bg-ink-soft text-white px-5 h-11 inline-flex items-center text-[11px] tracking-[0.2em] uppercase font-medium transition-colors"
          >
            Get Free Quote
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-1">
          <a
            href={telLink()}
            data-testid="mobile-call-quick"
            aria-label="Call now"
            className="p-2.5 text-ink"
          >
            <Phone size={18} />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="p-2.5 text-ink"
                aria-label="Open menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] bg-white p-6">
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `py-3 font-display text-xl border-b border-ink-line ${
                        n.to === "/epoxy-flooring"
                          ? "text-orange"
                          : isActive
                            ? "text-ink"
                            : "text-ink-mid"
                      }`
                    }
                    end={n.to === "/"}
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="mt-6 grid grid-cols-2 gap-2">
                  <a
                    href={telLink()}
                    data-testid="mobile-call-btn"
                    className="bg-ink text-white h-12 inline-flex items-center justify-center text-[11px] tracking-[0.2em] uppercase"
                  >
                    Call Now
                  </a>
                  <a
                    href={waLink()}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mobile-whatsapp-btn"
                    className="border border-ink text-ink h-12 inline-flex items-center justify-center text-[11px] tracking-[0.2em] uppercase"
                  >
                    WhatsApp
                  </a>
                </div>
                <Link
                  to="/contact"
                  data-testid="mobile-get-quote"
                  className="mt-2 bg-ink-soft text-white h-12 inline-flex items-center justify-center text-[11px] tracking-[0.2em] uppercase"
                >
                  Get Free Quote
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
